import {
  CancelRefundIntent,
  ItemChoice,
  REASON_NOTE_MAX,
  REASON_REQUIRING_NOTE,
  SelectionState,
  ValidationResult,
  amountForChoice,
  buildRequest,
  defaultOption,
  formatCents,
  formatDate,
  proratedBasisLabel,
  reasonsFor,
  targetItems,
  targetLabel,
  targetOptions,
} from './cancel-refund-panel.logic';
import {
  CancelRefundItem,
  CancelRefundOptions,
  CancelRefundResult,
  FamilyCancelMode,
  FamilyCancelOptions,
  FamilyCancelPlate,
  FamilyCancelRequest,
  FamilyCancelResult,
  RefundOption,
} from '../../../services/cancel-refund.service';

/**
 * CWS-1579 (CWS-1582 §6): the panel's rules for cancelling a family primary, kept pure like
 * cancel-refund-panel.logic. A primary with members picks Cancel all (every plate, each with its
 * own refund) or Cancel primary only (another member becomes the primary). One reason and one
 * "When" apply to every plate. The server re-checks everything here.
 */

/** Refund choice per plate, keyed by subscription id. */
export type PlateChoices = Record<string, ItemChoice>;

export interface FamilySelection {
  mode: FamilyCancelMode | null;
  newPrimaryMemberId: string | null;
  choices: PlateChoices;
}

export const FAMILY_MODE_COPY: Record<FamilyCancelMode, { label: string; sub: string }> = {
  all: {
    label: 'Cancel all plates',
    sub: 'Every membership in the family is cancelled.',
  },
  primary_only: {
    label: 'Cancel primary only',
    sub: 'Another member becomes the primary and pays full price from their next renewal.',
  },
};

const OPTION_NAMES: Record<RefundOption, string> = {
  none: 'No refund',
  prorated: 'Prorated refund',
  full: 'Full refund',
  custom: 'Custom amount',
};

/** The panel was opened to cancel a family primary that has other plates. */
export function isFamilyPrimaryCancel(
  options: CancelRefundOptions | null | undefined,
  intent: CancelRefundIntent
): boolean {
  return (
    intent === 'cancel' &&
    options?.family?.role === 'primary' &&
    Number(options.family.active_count ?? 0) > 1
  );
}

/** The membership lines one plate cancels. */
export function plateItems(plate: FamilyCancelPlate): CancelRefundItem[] {
  return targetItems(plate.options.items, 'cancel');
}

export function primaryPlate(family: FamilyCancelOptions): FamilyCancelPlate | undefined {
  return family.members.find(plate => plate.role === 'primary');
}

/** The plates the mode cancels: the primary alone, or every plate that can still be cancelled. */
export function platesInScope(
  family: FamilyCancelOptions | null,
  mode: FamilyCancelMode | null
): FamilyCancelPlate[] {
  if (!family || !mode) return [];
  if (mode === 'primary_only') {
    const primary = primaryPlate(family);
    return primary && plateItems(primary).length ? [primary] : [];
  }
  return family.members.filter(plate => !plate.already_ending && plateItems(plate).length > 0);
}

/** Plates already on their way out: on Cancel all they leave the family without a new cancel. */
export function endingPlates(
  family: FamilyCancelOptions | null,
  mode: FamilyCancelMode | null
): FamilyCancelPlate[] {
  if (!family || mode !== 'all') return [];
  return family.members.filter(plate => plate.already_ending);
}

/** The members who can take over as the primary. */
export function newPrimaryCandidates(family: FamilyCancelOptions | null): FamilyCancelPlate[] {
  return (family?.members ?? []).filter(plate => plate.role !== 'primary');
}

/**
 * Why a plate cannot become the new primary, or null when it can. A membership that is ending
 * would leave the family without a primary when it ends.
 */
export function primaryBlockReason(plate: FamilyCancelPlate): string | null {
  if (!plate.already_ending) return null;
  const ends = plate.options.membership?.cancel_at_period_end
    ? formatDate(plate.options.membership.current_period_end)
    : null;
  return ends ? `Ends ${ends} · can't be primary` : "Ending · can't be primary";
}

/** The members who can become the primary. */
export function eligiblePrimaries(family: FamilyCancelOptions | null): FamilyCancelPlate[] {
  return newPrimaryCandidates(family).filter(plate => !primaryBlockReason(plate));
}

export interface NewPrimaryOption {
  plate: FamilyCancelPlate;
  label: string;
  disabled: boolean;
}

/** Every member for the picker; one that cannot be the primary is greyed with the reason. */
export function newPrimaryOptions(family: FamilyCancelOptions | null): NewPrimaryOption[] {
  return newPrimaryCandidates(family).map(plate => {
    const reason = primaryBlockReason(plate);
    return {
      plate,
      label: reason ? `${plateLabel(plate)} — ${reason}` : plateLabel(plate),
      disabled: !!reason,
    };
  });
}

/** Chosen for staff when only one member can be the primary. */
export function defaultNewPrimary(family: FamilyCancelOptions | null): string | null {
  const eligible = eligiblePrimaries(family);
  return eligible.length === 1 ? eligible[0].member_id : null;
}

/**
 * The selection carried over a reload of the family. The mode and the new-primary pick are kept
 * only while the reloaded family still allows them (a pick may have left or be ending now);
 * every plate's refund choice starts again from its default, since the reload re-prices it.
 */
export function reconcileFamilySelection(
  previous: FamilySelection | null,
  family: FamilyCancelOptions
): FamilySelection {
  const mode =
    previous?.mode &&
    family.modes.includes(previous.mode) &&
    !familyModeBlockReason(family, previous.mode)
      ? previous.mode
      : null;
  const stillEligible = eligiblePrimaries(family).some(
    plate => plate.member_id === previous?.newPrimaryMemberId
  );
  const fallback = mode === 'primary_only' ? defaultNewPrimary(family) : null;
  return {
    mode,
    newPrimaryMemberId: stillEligible ? (previous?.newPrimaryMemberId ?? null) : fallback,
    choices: initialChoices(family),
  };
}

/** Why a mode cannot be chosen, or null. */
export function familyModeBlockReason(
  family: FamilyCancelOptions | null,
  mode: FamilyCancelMode
): string | null {
  if (mode !== 'primary_only') return null;
  if (newPrimaryCandidates(family).length < 2 || eligiblePrimaries(family).length > 0) return null;
  return 'Every other plate is already ending. Use Cancel all plates.';
}

export interface FamilyModeTile {
  mode: FamilyCancelMode;
  label: string;
  sub: string;
  disabled: boolean;
}

/** The mode tiles, a blocked one carrying its reason in place of the description. */
export function familyModeTiles(
  family: FamilyCancelOptions | null,
  modes: FamilyCancelMode[]
): FamilyModeTile[] {
  return modes.map(mode => {
    const reason = familyModeBlockReason(family, mode);
    return {
      mode,
      label: FAMILY_MODE_COPY[mode].label,
      sub: reason ?? FAMILY_MODE_COPY[mode].sub,
      disabled: !!reason,
    };
  });
}

/** A pick is needed when two or more plates stay; with one left the family simply ends. */
export function needsNewPrimary(
  family: FamilyCancelOptions | null,
  mode: FamilyCancelMode | null
): boolean {
  return mode === 'primary_only' && newPrimaryCandidates(family).length >= 2;
}

/** What happens to the rest of the family, in one line under the picker. */
export function primaryOnlyNote(family: FamilyCancelOptions | null): string {
  const others = newPrimaryCandidates(family).length;
  if (others <= 1) {
    return 'The family ends: the other plate continues on its own at full price from its next renewal.';
  }
  return 'The new primary pays full price from their next renewal. Everyone else keeps their family discount and their card.';
}

/** Each plate starts on its own default: no refund for a live membership. */
export function initialChoices(family: FamilyCancelOptions): PlateChoices {
  const choices: PlateChoices = {};
  for (const plate of family.members) {
    choices[plate.subscription_id] = { option: defaultOption(plateItems(plate)), customInput: '' };
  }
  return choices;
}

/** "ABC123 · Unlimited Monthly" */
export function plateLabel(plate: FamilyCancelPlate): string {
  const plateText = (plate.license_plate || '').trim() || 'No plate';
  const pkg = targetLabel(plateItems(plate));
  return pkg ? `${plateText} · ${pkg}` : plateText;
}

export function plateRoleLabel(plate: FamilyCancelPlate): string {
  return plate.role === 'primary' ? 'Primary' : 'Family';
}

/** The refund options one plate offers, worded with their amounts for its select. */
export function plateOptionLabels(
  plate: FamilyCancelPlate
): { option: RefundOption; label: string }[] {
  const items = plateItems(plate);
  return targetOptions(items).map(option => {
    if (option === 'none') return { option, label: OPTION_NAMES.none };
    const choice: ItemChoice = { option, customInput: '' };
    if (option === 'custom') {
      const max = items.reduce(
        (sum, item) => sum + Math.max(0, item.refund_amounts?.custom?.max_cents ?? 0),
        0
      );
      return {
        option,
        label: `${OPTION_NAMES.custom} (up to ${formatCents(Math.min(max, plate.options.refundable_cents))})`,
      };
    }
    const cents = amountForChoice(items, plate.options.refundable_cents, choice).cents;
    const basis = option === 'prorated' ? proratedBasisLabel(items[0]) : null;
    const detail = basis ? `${formatCents(cents)}, ${basis}` : formatCents(cents);
    return { option, label: `${OPTION_NAMES[option]} (${detail})` };
  });
}

export function plateRefund(
  plate: FamilyCancelPlate,
  choice: ItemChoice | undefined
): { cents: number; problem: string | null } {
  const items = plateItems(plate);
  const picked = choice ?? { option: defaultOption(items), customInput: '' };
  const { cents, problem } = amountForChoice(items, plate.options.refundable_cents, picked);
  return { cents: problem ? 0 : cents, problem: problem?.message ?? null };
}

export function familyRefundTotal(plates: FamilyCancelPlate[], choices: PlateChoices): number {
  return plates.reduce(
    (sum, plate) => sum + plateRefund(plate, choices[plate.subscription_id]).cents,
    0
  );
}

/** A plate paid in cash is refunded by hand; the operator confirms it once for all of them. */
export function familyNeedsCash(plates: FamilyCancelPlate[], choices: PlateChoices): boolean {
  return plates.some(
    plate =>
      plate.options.refund_method === 'cash' &&
      plateRefund(plate, choices[plate.subscription_id]).cents > 0
  );
}

export function validateFamily(
  family: FamilyCancelOptions | null,
  selection: FamilySelection,
  shared: SelectionState
): ValidationResult {
  if (!family) return { ok: false, reason: null };
  if (!selection.mode) return { ok: false, reason: 'Choose Cancel all or Cancel primary only' };
  const blocked = familyModeBlockReason(family, selection.mode);
  if (blocked) return { ok: false, reason: blocked };
  const plates = platesInScope(family, selection.mode);
  if (!plates.length) return { ok: false, reason: 'Nothing in this family can be cancelled' };
  if (needsNewPrimary(family, selection.mode)) {
    const picked = eligiblePrimaries(family).some(
      plate => plate.member_id === selection.newPrimaryMemberId
    );
    if (!picked) return { ok: false, reason: 'Choose the new primary' };
  }
  for (const plate of plates) {
    const choice = selection.choices[plate.subscription_id];
    if (!choice || !targetOptions(plateItems(plate)).includes(choice.option)) {
      return { ok: false, reason: `Choose a refund for ${plateLabel(plate)}` };
    }
    const { problem } = plateRefund(plate, choice);
    if (problem) return { ok: false, reason: `${plateLabel(plate)}: ${problem}` };
  }
  const reasons = reasonsFor(plates[0].options, 'cancel');
  if (!shared.reasonCode || !reasons.some(reason => reason.code === shared.reasonCode)) {
    return { ok: false, reason: 'Choose a reason' };
  }
  if (shared.reasonCode === REASON_REQUIRING_NOTE && !shared.reasonNote.trim()) {
    return { ok: false, reason: 'Say what the reason is' };
  }
  if (shared.reasonNote.length > REASON_NOTE_MAX) {
    return { ok: false, reason: `Keep the note under ${REASON_NOTE_MAX} characters` };
  }
  if (familyNeedsCash(plates, selection.choices) && !shared.cashHandedOver) {
    return { ok: false, reason: 'Confirm the cash was handed back' };
  }
  return { ok: true, reason: null };
}

/** Each plate is built exactly as a single cancel would be, then gathered into one request. */
export function buildFamilyRequest(args: {
  family: FamilyCancelOptions;
  selection: FamilySelection;
  shared: SelectionState;
  requestId: string;
  locationId?: string | null;
}): FamilyCancelRequest {
  const { family, selection, shared, requestId, locationId } = args;
  const mode = selection.mode as FamilyCancelMode;
  const plates = platesInScope(family, mode).map(plate => {
    const single = buildRequest(
      plate.options,
      plateItems(plate),
      { ...shared, choice: selection.choices[plate.subscription_id] },
      requestId,
      { subscriptionId: plate.subscription_id }
    );
    return { subscription_id: plate.subscription_id, version: single.version, items: single.items };
  });
  const primary = primaryPlate(family);
  const body: FamilyCancelRequest = {
    request_id: requestId,
    family_version: family.family_version,
    group_id: family.group_id,
    subscription_id: primary?.subscription_id ?? '',
    mode,
    reason_code: shared.reasonCode ?? '',
    cancel_timing: shared.cancelTiming,
    plates,
  };
  if (needsNewPrimary(family, mode)) body.new_primary_member_id = selection.newPrimaryMemberId;
  if (locationId) body.location_id = locationId;
  const note = shared.reasonNote.trim();
  if (note) body.reason_note = note;
  return body;
}

/** "Cancel 3 memberships" / "Refund $40.00 & cancel primary" */
export function familyConfirmLabel(
  mode: FamilyCancelMode | null,
  plates: number,
  refund: number
): string {
  if (!mode) return 'Confirm';
  const what = mode === 'all' ? `${plates} membership${plates === 1 ? '' : 's'}` : 'primary';
  return refund > 0 ? `Refund ${formatCents(refund)} & cancel ${what}` : `Cancel ${what}`;
}

/** The host's toast line for a family cancel. */
export function familySummary(
  result: FamilyCancelResult,
  family: FamilyCancelOptions | null
): string {
  const byId = new Map((family?.members ?? []).map(plate => [plate.subscription_id, plate]));
  const name = (id: string) => {
    const plate = byId.get(id);
    return plate ? (plate.license_plate || '').trim() || 'A plate' : 'A plate';
  };
  const refunded = result.plates.reduce(
    (sum, plate) => sum + (plate.refund?.refunded_cents ?? 0),
    0
  );
  const money = refunded > 0 ? ` ${formatCents(refunded)} refunded in total.` : '';
  const cancelled = result.plates.filter(plate => plate.cancelled);
  const notDone = result.plates.filter(plate => !plate.cancelled);

  if (!cancelled.length) {
    return 'Nothing was cancelled. Close and open the family again to see why.';
  }
  const parts: string[] = [];
  if (result.mode === 'primary_only') {
    const newPrimary = result.family.new_primary_member_id
      ? family?.members.find(plate => plate.member_id === result.family.new_primary_member_id)
      : null;
    parts.push(
      newPrimary
        ? `Primary cancelled; ${name(newPrimary.subscription_id)} is the new primary.`
        : 'Primary cancelled; the family has ended.'
    );
  } else {
    parts.push(`${cancelled.length} membership${cancelled.length === 1 ? '' : 's'} cancelled.`);
  }
  if (notDone.length) {
    parts.push(`Not cancelled: ${notDone.map(plate => name(plate.subscription_id)).join(', ')}.`);
  }
  // A cancelled plate whose refund failed still owes the customer; say so and where to retry.
  const refundFailed = result.plates.filter(plate => plate.status === 'refund_failed');
  if (refundFailed.length) {
    const owed = refundFailed.reduce(
      (sum, plate) =>
        sum +
        Math.max((plate.refund?.requested_cents ?? 0) - (plate.refund?.refunded_cents ?? 0), 0),
      0
    );
    parts.push(
      `Refund failed for ${refundFailed.map(plate => name(plate.subscription_id)).join(', ')}: ${formatCents(owed)} is still owed — use Retry refund on the row.`
    );
  }
  if (result.family.pending) {
    parts.push('The family could not be updated yet; open it and confirm again to finish.');
  }
  return `${parts.join(' ')}${money}`;
}

/**
 * The single-cancel shape the hosts already read (status, action), so the Orders list and the
 * Customers tab handle a family cancel without knowing about families.
 */
export function familyDoneResult(result: FamilyCancelResult): CancelRefundResult {
  const anyCancelled = result.plates.some(plate => plate.cancelled);
  const refundFailed = result.plates.some(plate => plate.status === 'refund_failed');
  const status = !anyCancelled ? 'failed' : refundFailed ? 'refund_failed' : 'completed';
  const refunded = result.plates.reduce(
    (sum, plate) => sum + (plate.refund?.refunded_cents ?? 0),
    0
  );
  const requested = result.plates.reduce(
    (sum, plate) => sum + (plate.refund?.requested_cents ?? 0),
    0
  );
  return {
    replayed: result.retry,
    cancellation: { id: '', status, action: 'cancel' },
    status,
    order_status: null,
    refund: {
      method: null,
      requested_cents: requested,
      refunded_cents: refunded,
      capped_at_cents: null,
      stripe_refund_id: null,
      failed: refundFailed ? 'refund_failed' : null,
    },
    membership: null,
    items: [],
    family: result.family,
  };
}

/**
 * A plain cancel whose membership is cancelled but whose family update did not finish: the same
 * request sent again replays the cancel and finishes the family.
 */
export function familyUpdatePending(result: CancelRefundResult | null | undefined): boolean {
  return result?.family?.pending === true;
}
